import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  FileText,
  Flame,
  Headphones,
  Lock,
  PlayCircle,
  Video,
  ClipboardCheck,
  Info,
  ChevronsDown,
  ChevronsUp,
  MonitorPlay,
  Package,
  Trophy,
  Star
} from 'lucide-react';
import { ContentItem } from '@/types/ai-learn.types';
import { formatDuration } from '@/utils/timeFormat';
import toast from 'react-hot-toast';
import CourseOverviewModal from './CourseOverviewModal';
import ReviewModal from '@/components/reviews/ReviewModal';
import { ReviewType } from '@/types/review.types';

interface CourseNavigationProps {
  course: any;
  modules: any[];
  selectedModule: any;
  selectedModuleContent: any;
  selectedContent: ContentItem | null;
  expandedModules: { [key: string]: boolean };
  completedModules: Set<string>;
  loadingModuleContent: boolean;
  userLevel: number;
  userXP: number;
  learningStreak: number;
  courseProgress: number;
  isLastContentItem?: boolean;
  isCourseCompleted?: boolean;
  /** True once the certificate actually exists. Completion issues it asynchronously,
   *  so a completed course is not proof the certificate is available yet. */
  certificateIssued?: boolean;
  userEmail?: string;
  userName?: string;
  completingCourse?: boolean;
  showingCourseOverview?: boolean;
  moduleContentCache: { [moduleId: string]: any };
  progressData?: any;
  isLearner?: boolean;
  isScormCourse?: boolean;
  /** From the course completion gate. Defaults true so callers that omit it keep the tick. */
  manualCompletionEnabled?: boolean;
  /** Assessments the course's completion config requires a pass on. */
  requiredAssessmentIds?: Set<string>;
  onModuleSelect: (module: any) => void;
  onContentItemSelect: (item: ContentItem) => void;
  onToggleModule: (moduleId: string) => void;
  onSetAllExpandedModules?: (expanded: { [key: string]: boolean }) => void;
  onCompleteCourse?: () => void;
  onShowCourseOverview?: () => void;
  onContentComplete?: (contentId: string) => void;
  onDownloadCertificate?: () => void;
}

export default function CourseNavigation({
  course,
  modules,
  selectedModule,
  selectedModuleContent: _selectedModuleContent,
  selectedContent,
  expandedModules,
  completedModules,
  loadingModuleContent,
  userLevel,
  userXP,
  learningStreak,
  courseProgress,
  isLastContentItem: _isLastContentItem,
  isCourseCompleted: _isCourseCompleted,
  certificateIssued,
  userEmail: _userEmail,
  userName,
  completingCourse: _completingCourse,
  showingCourseOverview,
  moduleContentCache,
  progressData,
  isLearner = true,
  isScormCourse = false,
  manualCompletionEnabled = true,
  requiredAssessmentIds,
  onModuleSelect,
  onContentItemSelect,
  onToggleModule,
  onSetAllExpandedModules,
  onCompleteCourse: _onCompleteCourse,
  onShowCourseOverview,
  onContentComplete,
  onDownloadCertificate: _onDownloadCertificate
}: CourseNavigationProps) {
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const navigate = useNavigate();

  // Courses can withdraw the manual tick, leaving completion to time spent. The
  // server already forces this off for SCORM.
  const canMarkLessonComplete = manualCompletionEnabled && !!onContentComplete;

  // "Mandatory" label for assessments the learner must pass to complete the course.
  const renderRequiredMark = (item?: ContentItem) =>
    item?.type === 'assessment' && item.assessment_id && requiredAssessmentIds?.has(item.assessment_id) ? (
      <span
        className="ml-1.5 inline-block align-middle px-1.5 py-px rounded-full border border-red-200 bg-red-50 text-[10px] font-semibold leading-4 text-red-700"
        title="Required to complete the course"
      >
        Mandatory
      </span>
    ) : null;

  // Helper function to check if a module is locked (defined first so other functions can use it)
  const isModuleLocked = (module: any, _moduleIndex: number): boolean => {
    // Admin preview mode: all modules are unlocked
    if (!isLearner) {
      return false;
    }

    // Course assessments are never locked
    if (module.is_course_assessment || module.is_course_pre_assessment) {
      return false;
    }

    // Filter out course assessments to get actual module list
    const actualModules = modules.filter(m => !m.is_course_assessment && !m.is_course_pre_assessment);
    const actualModuleIndex = actualModules.findIndex(m => m._id === module._id);

    // First actual module is always unlocked
    if (actualModuleIndex === 0) return false;
    if (actualModuleIndex === -1) return false; // Module not found, don't lock

    // Module is unlocked if the previous actual module is completed
    const previousModule = actualModules[actualModuleIndex - 1];
    if (!previousModule) return false;

    // Check if previous module is completed
    const previousModuleProgressFromApi = progressData?.module_progress?.find(
      (mp: any) => mp.module_id === previousModule._id
    );
    const isPreviousModuleCompleted =
      completedModules.has(previousModule._id) ||
      previousModuleProgressFromApi?.status === 'COMPLETED';

    return !isPreviousModuleCompleted;
  };

  // Expand/Collapse All functionality
  const handleExpandAll = () => {
    if (!onSetAllExpandedModules) return;

    const allExpanded: { [key: string]: boolean } = { ...expandedModules };
    modules.forEach((module, index) => {
      // Only expand unlocked modules
      if (!isModuleLocked(module, index)) {
        allExpanded[module._id] = true;
      }
    });
    onSetAllExpandedModules(allExpanded);
  };

  const handleCollapseAll = () => {
    if (!onSetAllExpandedModules) return;

    const allCollapsed: { [key: string]: boolean } = { ...expandedModules };
    modules.forEach(module => {
      allCollapsed[module._id] = false;
    });
    onSetAllExpandedModules(allCollapsed);
  };

  // Check if all unlocked modules are expanded or collapsed
  const unlockedModules = modules.filter((module, index) => !isModuleLocked(module, index));
  const allExpanded = unlockedModules.length > 0 && unlockedModules.every(module => expandedModules[module._id]);
  const allCollapsed = unlockedModules.length > 0 && unlockedModules.every(module => !expandedModules[module._id]);


  const getContentIcon = (type: string, isSelected: boolean, isCompleted: boolean) => {
    // Selected: blue, Completed: green, Not started: gray
    const iconColor = isSelected ? 'text-brand-blue-500' : isCompleted ? 'text-green-500' : 'text-gray-400';

    switch (type) {
      case 'video_presentation': return <Video className={`w-4 h-4 ${iconColor}`} />;
      case 'avatar_video':
      case 'avatar_presentation': return <MonitorPlay className={`w-4 h-4 ${iconColor}`} />;
      case 'html_with_audio': return <Headphones className={`w-4 h-4 ${iconColor}`} />;
      case 'html': return <BookOpen className={`w-4 h-4 ${iconColor}`} />;
      case 'assessment': return <ClipboardCheck className={`w-4 h-4 ${iconColor}`} />;
      case 'scorm': return <Package className={`w-4 h-4 ${iconColor}`} />;
      default: return <Circle className={`w-4 h-4 ${iconColor}`} />;
    }
  };

  // Helper function to check if a content item (lesson/assessment) is locked
  const isContentItemLocked = (item: ContentItem, allContentItems: ContentItem[], moduleId: string): boolean => {
    // Admin preview mode: all content items are unlocked
    if (!isLearner) {
      return false;
    }

    const isAssessment = item.type === 'assessment';
    const assessmentType = item.assessment_type;

    // A lesson opens once the lesson before it is completed; the first one is always open.
    // Shared with lesson-level PRE-assessments below, which must open at the very same moment.
    const isLessonLocked = (lesson: ContentItem): boolean => {
      const lessons = allContentItems.filter(ci => ci.type !== 'assessment');
      const lessonIndex = lessons.indexOf(lesson);
      // First lesson, or a lesson not in this list — nothing to gate on.
      if (lessonIndex <= 0) return false;
      return !isContentCompleted(lessons[lessonIndex - 1]._id, moduleId);
    };

    // PRE-ASSESSMENTS
    if (isAssessment && assessmentType === 'PRE') {
      // Module-level pre-assessment introduces the whole module, not one lesson — the module's
      // own lock already gates it, so it stays open.
      if (item.assessment_level !== 'CONTENT_ITEM') {
        return false;
      }

      // A lesson's pre-assessment belongs to the lesson it precedes (the tree is built
      // pre → lesson → post), so it unlocks with that lesson. Leaving these permanently open
      // let a learner sit the pre-test for lesson 5 while lesson 2 was still locked.
      const itemIndex = allContentItems.indexOf(item);
      const owner_lesson = allContentItems
        .slice(itemIndex + 1)
        .find(ci => ci.type !== 'assessment');

      return owner_lesson ? isLessonLocked(owner_lesson) : false;
    }

    // POST-ASSESSMENTS: Locked until completion requirement is met
    if (isAssessment && assessmentType === 'POST') {
      const assessmentLevel = item.assessment_level;

      // Lesson post-assessment: Locked until the lesson is completed
      if (assessmentLevel === 'CONTENT_ITEM') {
        // Find the lesson this post-assessment belongs to (it comes right after the lesson)
        const itemIndex = allContentItems.indexOf(item);
        if (itemIndex > 0) {
          const previousItem = allContentItems[itemIndex - 1];
          // Locked if the previous lesson is NOT completed
          return !isContentCompleted(previousItem._id, moduleId);
        }
        return true; // Locked by default if can't find previous item
      }

      // Module post-assessment: Locked until ALL lessons in the module are completed
      if (assessmentLevel === 'MODULE') {
        const allLessons = allContentItems.filter(ci =>
          ci.type !== 'assessment' // Only count actual lessons, not assessments
        );
        const allLessonsCompleted = allLessons.every(lesson =>
          isContentCompleted(lesson._id, moduleId)
        );
        return !allLessonsCompleted; // Locked if not all lessons completed
      }
    }

    // REGULAR LESSONS: Progressive unlock - first lesson unlocked, rest locked until previous is completed
    if (!isAssessment) {
      return isLessonLocked(item);
    }

    return false; // Default: unlocked
  };

  // Check if content item is completed based on progress data (Memoized for performance)
  const isContentCompleted = React.useMemo(() => {
    const completionMap = new Map<string, boolean>();

    if (progressData?.module_progress) {
      progressData.module_progress.forEach((mp: any) => {
        if (mp.lesson_progress) {
          mp.lesson_progress.forEach((lp: any) => {
            const key = `${mp.module_id}-${lp.lesson_id}`;
            completionMap.set(key, lp.status === 'COMPLETED');
          });
        }
      });
    }

    return (contentId: string, moduleId: string): boolean => {
      return completionMap.get(`${moduleId}-${contentId}`) || false;
    };
  }, [progressData?.module_progress]);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Progress Header - Clickable */}
      <button
        onClick={onShowCourseOverview}
        className="p-3 bg-gradient-to-r from-brand-blue-50 to-brand-purple-50 border-b border-gray-200 transition-all hover:from-brand-blue-100 hover:to-brand-purple-100 text-left"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-brand-blue-600 rounded-lg">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Course Progress</p>
              <p className="text-xs text-gray-600">{Math.round(courseProgress)}% Complete</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              onClick={(e) => {
                e.stopPropagation();
                setShowCourseModal(true);
              }}
              className="p-2 hover:bg-white/50 rounded-lg transition-colors cursor-pointer"
              title="View Course Overview"
            >
              <Info className="w-4 h-4 text-brand-blue-600" />
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-brand-blue-600">{userXP}</p>
              <p className="text-xs text-gray-600">XP</p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-brand-blue-500 to-brand-purple-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${courseProgress}%` }}
          />
        </div>

        {/* Stats */}
        <div className="flex justify-between mt-2 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <Flame className="w-3 h-3 text-orange-500" />
            {learningStreak} day streak
          </span>
          <span>Level {userLevel}</span>
        </div>
      </button>

      {/* Expand/Collapse All Controls */}
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600">Modules</span>
          <div className="flex gap-1">
            <button
              onClick={handleExpandAll}
              disabled={allExpanded}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-all ${
                allExpanded
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-brand-blue-600 hover:bg-brand-blue-50'
              }`}
              title="Expand All"
            >
              <ChevronsDown className="w-3 h-3" />
              <span>Expand All</span>
            </button>
            <button
              onClick={handleCollapseAll}
              disabled={allCollapsed}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-all ${
                allCollapsed
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-brand-blue-600 hover:bg-brand-blue-50'
              }`}
              title="Collapse All"
            >
              <ChevronsUp className="w-3 h-3" />
              <span>Collapse All</span>
            </button>
          </div>
        </div>
      </div>

      {/* Module List */}
      <div className="flex-1 overflow-y-auto scrollbar-none" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <div className="p-1">
          {/* Course Overview Entry */}
          <div className="mb-2">
            <button
              onClick={onShowCourseOverview}
              className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all ${
                showingCourseOverview
                  ? 'bg-brand-blue-100 border-2 border-brand-blue-500 shadow-md'
                  : 'hover:bg-gray-50 border-2 border-transparent'
              }`}
            >
              <div className="flex-shrink-0">
                <Info className={`w-5 h-5 ${showingCourseOverview ? 'text-brand-blue-600' : 'text-gray-500'}`} />
              </div>
              <div className="flex-1 text-left">
                <h3 className={`font-medium text-xs leading-tight ${
                  showingCourseOverview ? 'text-brand-blue-900' : 'text-gray-900'
                }`}>
                  Course Overview
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">View course details</p>
              </div>
            </button>
          </div>

          {modules.map((module, moduleIndex) => {
            // Check both local state and progressData for completion status
            const moduleProgressFromApi = progressData?.module_progress?.find(
              (mp: any) => mp.module_id === module._id
            );
            const isCompleted = completedModules.has(module._id) || moduleProgressFromApi?.status === 'COMPLETED';
            const isSelected = !showingCourseOverview && selectedModule?._id === module._id;
            const isExpanded = expandedModules[module._id];
            const isLocked = isModuleLocked(module, moduleIndex);
            const isCourseAssessment = module.is_course_assessment;
            const isCoursePreAssessment = module.is_course_pre_assessment;

            // For course-level assessments, render as direct clickable item
            if (isCourseAssessment || isCoursePreAssessment) {
              const assessmentItem = module.content_items?.[0]; // Get the assessment content item
              if (!assessmentItem) return null;

              // Check if course POST-assessment is locked (locked until all modules are completed)
              let isCourseAssessmentLocked = false;
              if (isCourseAssessment) {
                // Admin preview mode: course assessments are never locked
                if (!isLearner) {
                  isCourseAssessmentLocked = false;
                } else {
                  const actualModules = modules.filter(m => !m.is_course_assessment && !m.is_course_pre_assessment);
                  const allModulesCompleted = actualModules.every(m => {
                    const modProgress = progressData?.module_progress?.find((mp: any) => mp.module_id === m._id);
                    return completedModules.has(m._id) || modProgress?.status === 'COMPLETED';
                  });

                  // Lock if not all modules completed
                  isCourseAssessmentLocked = !allModulesCompleted;
                }
              }

              // Course PRE-assessment is never locked

              const isContentSelected = !showingCourseOverview && selectedContent?._id === assessmentItem._id;

              return (
                <div key={module._id} className="mb-1">
                  <button
                    onClick={() => {
                      if (!isCourseAssessmentLocked) {
                        onContentItemSelect(assessmentItem);
                      } else {
                        // Show toast when user tries to access locked course assessment
                        toast.error('Complete all modules to unlock this assessment', {
                          icon: '🔒',
                          duration: 3000
                        });
                      }
                    }}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all text-left ${
                      isContentSelected
                        ? 'bg-brand-blue-100 border-2 border-brand-blue-500 shadow-md'
                        : isCourseAssessmentLocked
                        ? 'cursor-pointer bg-gray-50/50 border-2 border-gray-200 opacity-60'
                        : 'hover:bg-gray-50 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {isCourseAssessmentLocked ? (
                        <Lock className="w-5 h-5 text-gray-400" />
                      ) : isCoursePreAssessment ? (
                        <ClipboardCheck className={`w-5 h-5 ${isContentSelected ? 'text-blue-600' : 'text-blue-500'}`} />
                      ) : (
                        <ClipboardCheck className={`w-5 h-5 ${isContentSelected ? 'text-purple-600' : 'text-purple-500'}`} />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-medium text-xs leading-tight ${
                        isCourseAssessmentLocked ? 'text-gray-400' : 'text-gray-900'
                      }`}
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                        {module.title}
                        {renderRequiredMark(assessmentItem)}
                      </h3>
                      {assessmentItem.estimated_time && (
                        <p className={`text-xs flex items-center gap-1 mt-0.5 ${
                          isCourseAssessmentLocked ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          <Clock className="w-3 h-3" />
                          {formatDuration(assessmentItem.estimated_time)}
                        </p>
                      )}
                      {isCourseAssessmentLocked && (
                        <p className="text-xs text-gray-400 italic mt-0.5">
                          Locked - Complete all modules
                        </p>
                      )}
                    </div>
                    {isContentSelected && !isCourseAssessmentLocked && (
                      <PlayCircle className="w-4 h-4 text-brand-purple-600 flex-shrink-0" />
                    )}
                  </button>
                </div>
              );
            }

            // For regular modules, render as expandable items
            return (
              <div key={module._id} className="mb-1">
                {/* Module Header */}
                <button
                  onClick={() => {
                    if (!isLocked) {
                      if (isExpanded || isSelected) {
                        onToggleModule(module._id);
                      } else {
                        // A different module — open it, as before.
                        onModuleSelect(module);
                      }
                    } else {
                      // Show toast when user tries to access locked module
                      toast.error('Complete the previous module to unlock this one', {
                        icon: '🔒',
                        duration: 3000
                      });
                    }
                  }}
                  className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all ${
                    isSelected
                      ? 'bg-brand-blue-100 border-2 border-brand-blue-500 shadow-md'
                      : isLocked
                      ? 'text-gray-400 cursor-pointer bg-gray-50/50 border-2 border-gray-200 opacity-60'
                      : 'hover:bg-gray-50 border-2 border-transparent'
                  }`}
                >
                  {/* Status Icon - same box as the lessons below it, one size up */}
                  <div className="flex-shrink-0">
                    {isLocked ? (
                      <Lock className="w-5 h-5 text-gray-400" />
                    ) : (
                      <span
                        className={`flex items-center justify-center w-5 h-5 rounded-[3px] border-2 transition-colors ${
                          isCompleted
                            ? 'bg-green-600 border-green-600'
                            : isSelected
                            ? 'border-brand-blue-500'
                            : 'border-gray-300'
                        }`}
                      >
                        {isCompleted && (
                          <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                        )}
                      </span>
                    )}
                  </div>

                  {/* Module Info */}
                  <div className="flex-1 text-left">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className={`font-medium text-xs leading-tight ${
                          isLocked ? 'text-gray-400' : 'text-gray-900'
                        }`}
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          Module {module.module_number}: {module.title}
                        </h3>
                        {isLocked && (
                          <p className="text-xs text-gray-400 mt-0.5 italic">
                            Complete previous module to unlock
                          </p>
                        )}
                      </div>
                      {!isLocked && (
                        <div className="flex items-center gap-1">
                          {loadingModuleContent && isSelected ? (
                            <div className="w-4 h-4 border-2 border-brand-blue-600 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )
                          )}
                        </div>
                      )}
                    </div>
                    {!isScormCourse && !isLocked && module.estimated_duration && (
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" />
                        {formatDuration(module.estimated_duration)}
                      </p>
                    )}
                  </div>
                </button>

                {/* Content Items (includes assessments) */}
                {isExpanded && (
                  <div className="mt-1 space-y-1">
                    {(() => {
                      // For course assessments (pre/post), use inline content_items
                      if (module.content_items) {
                        return module.content_items;
                      }
                      // For regular modules, use cache
                      const cachedModule = moduleContentCache[module._id];
                      return cachedModule?.module?.content_items || [];
                    })()
                    .map((item: ContentItem) => {
                      // Get all content items to check lock status
                      const allItems = module.content_items ||
                        moduleContentCache[module._id]?.module?.content_items || [];
                      const isItemLocked = isContentItemLocked(item, allItems, module._id);
                      const isContentSelected = !showingCourseOverview && selectedContent?._id === item._id;
                      const isAssessment = item.type === 'assessment';
                      const isModuleLevelAssessment = isAssessment && item.assessment_level === 'MODULE';
                      const shouldIndent = !isModuleLevelAssessment;
                      const contentCompleted = isContentCompleted(item._id, module._id);

                      return (
                        <div
                          key={item._id}
                          className={shouldIndent ? 'pl-6' : ''}
                        >
                          <div className="flex items-center gap-1">
                            {/* Manual Completion Checkbox - leads the row, ahead of the
                                lesson itself. Hidden when locked. Where manual completion
                                is off, a completed lesson still shows its tick as a
                                read-only indicator. */}
                            {!isAssessment && !isItemLocked && (canMarkLessonComplete || contentCompleted) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (canMarkLessonComplete && !contentCompleted) {
                                    onContentComplete!(item._id);
                                  }
                                }}
                                disabled={contentCompleted || !canMarkLessonComplete}
                                className={`flex-shrink-0 p-1 rounded ${
                                  contentCompleted ? 'cursor-default' : ''
                                }`}
                                title={contentCompleted ? 'Completed' : 'Mark as complete'}
                              >
                                <span
                                  className={`flex items-center justify-center w-4 h-4 rounded-[3px] border-2 transition-colors ${
                                    contentCompleted
                                      ? 'bg-green-600 border-green-600'
                                      : 'border-gray-300 hover:border-green-600'
                                  }`}
                                >
                                  {contentCompleted && (
                                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                                  )}
                                </span>
                              </button>
                            )}

                            <button
                              onClick={() => {
                                if (!isItemLocked) {
                                  onContentItemSelect(item);
                                } else {
                                  // Show toast when user tries to access locked content
                                  toast.error(
                                    isAssessment
                                      ? 'Complete the required lessons to unlock this assessment'
                                      : 'Complete the previous lesson to unlock this one',
                                    {
                                      icon: '🔒',
                                      duration: 3000
                                    }
                                  );
                                }
                              }}
                              className={`flex-1 flex items-center gap-2 p-1.5 rounded-lg transition-all text-left ${
                                isContentSelected
                                  ? 'bg-brand-blue-100 border-2 border-brand-blue-500 shadow-md'
                                  : isItemLocked
                                  ? 'cursor-pointer bg-gray-50/50 border border-gray-200 opacity-60'
                                  : 'hover:bg-gray-50 border border-transparent'
                              }`}
                            >
                              <div className="flex-shrink-0">
                                {isItemLocked ? (
                                  <Lock className="w-4 h-4 text-gray-400" />
                                ) : (
                                  getContentIcon(item.type, isContentSelected, contentCompleted)
                                )}
                              </div>
                              <div className="flex-1">
                                <p className={`text-xs font-medium leading-tight ${
                                  isItemLocked ? 'text-gray-400' : 'text-gray-900'
                                }`}
                                   style={{
                                     display: '-webkit-box',
                                     WebkitLineClamp: 2,
                                     WebkitBoxOrient: 'vertical',
                                     overflow: 'hidden'
                                   }}>{item.title}{renderRequiredMark(item)}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {!isScormCourse && (item.duration || item.estimated_time) && (
                                    <span className={`text-xs flex items-center gap-1 ${
                                      isItemLocked ? 'text-gray-400' : 'text-gray-500'
                                    }`}>
                                      <Clock className="w-3 h-3" />
                                      {formatDuration(item.duration || item.estimated_time)}
                                    </span>
                                  )}
                                  {isItemLocked && (
                                    <span className="text-xs text-gray-400 italic">
                                      Locked
                                    </span>
                                  )}
                                </div>
                              </div>
                              {isContentSelected && !isItemLocked && (
                                <PlayCircle className="w-4 h-4 text-brand-purple-600 flex-shrink-0" />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Write a Review Section - Always visible for learners */}
        {isLearner && (
          <div className="p-3 mt-3 border-t border-gray-200">
            <button
              onClick={() => setShowReviewModal(true)}
              className="w-full bg-gradient-to-r from-brand-yellow-500 to-brand-orange-500 hover:from-brand-yellow-600 hover:to-brand-orange-600 text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              <Star className="w-4 h-4" />
              Write a Review
            </button>
          </div>
        )}

        {/* Course Completion Section — shown only once the certificate has actually
            been issued. That is the point at which the course is fully done: the
            certificate is generated asynchronously after completion, so neither 100%
            progress nor a COMPLETED status is sufficient on its own. There is no
            manual "Complete Course" button: completion is earned by finishing the
            lessons and passing any required assessments, never by clicking. */}
        {certificateIssued && (
          <div className="p-3 mt-3 border-t border-gray-200">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">Course Completed!</h3>
                <p className="text-xs text-gray-600 mb-3">
                  Congratulations on completing this course! Your certificate is available via email
                  and on the Achievements page.
                </p>

                <button
                  onClick={() => navigate('/learn/my-achievements')}
                  className="w-full bg-gradient-to-r from-brand-green-500 to-brand-green-700 hover:from-brand-green-600 hover:to-brand-green-600 text-white font-semibold py-2 px-4 rounded-lg text-sm transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  <Trophy className="w-4 h-4" />
                  View My Achievements
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Course Overview Modal */}
      <CourseOverviewModal
        course={course}
        isOpen={showCourseModal}
        onClose={() => setShowCourseModal(false)}
        onSelectModule={onModuleSelect}
        isScormCourse={isScormCourse}
      />

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        targetId={course?._id || course?.id || ''}
        targetTitle={course?.title || ''}
        targetType={ReviewType.COURSE}
        onSuccess={() => setShowReviewModal(false)}
      />
    </div>
  );
}